import React, { createContext, useContext, useEffect, useState } from "react";
import { onAuthStateChanged, createUserWithEmailAndPassword, signInWithEmailAndPassword, signOut, updateProfile } from "firebase/auth";
import { auth, db } from "@/config/firebase";
import { doc, getDoc, serverTimestamp, setDoc } from "firebase/firestore";
import { getFirestore, doc as fsDoc, getDoc as fsGetDoc } from "firebase/firestore";

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [role, setRole] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, async (u) => {
      if (u) {
        setUser(u);
        // 🔧 CRITICAL FIX: Set loading to false IMMEDIATELY after auth resolves
        // Don't wait for Firestore role fetch to complete
        setLoading(false);
        
        // Fetch role asynchronously (non-blocking)
        try {
          const ref = doc(db, "users", u.uid);
          const snap = await getDoc(ref);
          if (snap.exists()) {
            setRole(snap.data().role || "fan");
          } else {
            await setDoc(ref, {
              uid: u.uid,
              email: u.email,
              display_name: u.displayName || "",
              role: "fan",
              created_at: serverTimestamp(),
            });
            setRole("fan");
          }
        } catch (e) {
          console.error("Fehler beim Laden der User-Role:", e);
          setRole("fan"); // Fallback zu "fan" bei Fehler
        }
      } else {
        setUser(null);
        setRole(null);
        setLoading(false);
      }
    });
    return () => unsub();
  }, []);

  const register = async ({ email, password, displayName, role = "fan" }) => {
    const creds = await createUserWithEmailAndPassword(auth, email, password);
    if (displayName) await updateProfile(creds.user, { displayName });
    await setDoc(doc(db, "users", creds.user.uid), {
      uid: creds.user.uid,
      auth_uid: creds.user.uid,
      email,
      display_name: displayName || "",
      role: role || "fan", // fan, sparring, player, admin
      created_at: serverTimestamp(),
      // Profil-Pflichtfelder
      height: null,
      weight: null,
      position: null,
      profile_reminder_count: 0,
    }, { merge: true });
    return creds.user;
  };

  const loginWithIdentifier = async (identifier, password) => {
    const ident = String(identifier || "").trim();
    if (!ident) throw new Error("identifier required");
    // If it's an email, just use it
    if (ident.includes("@")) return signInWithEmailAndPassword(auth, ident, password);
    const key = ident.toLowerCase();
    let emailToUse = `${key}@rhein-stars.app`;
    try {
      const ref = fsDoc(getFirestore(), "user_logins", key);
      const snap = await fsGetDoc(ref);
      if (snap.exists()) {
        const data = snap.data();
        if (typeof data.email === 'string' && data.email) emailToUse = data.email;
        else if (typeof data.uid === 'string' && data.uid) {
          // keep placeholder pattern
        }
      }
    } catch (_) { /* fallback to placeholder */ }
    return signInWithEmailAndPassword(auth, emailToUse, password);
  };

  const login = (email, password) => signInWithEmailAndPassword(auth, email, password);
  const logout = () => signOut(auth);

  return (
    <AuthContext.Provider value={{ user, role, loading, register, login, loginWithIdentifier, logout }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
};
